/*
 * Which brand a page belongs to.
 *
 * Two products share one codebase, one build and one Express process:
 *
 *   property-people.co.uk   the estate agency, and its own report tool
 *   doorstepdata.co.uk      the standalone product, and the Android app
 *
 * THIS IS NOT A GENERAL HOST-AWARE ORIGIN, and deliberately not. Almost
 * everything on this site belongs to Property People unconditionally - the
 * listings, the blog, the area pages, the guides - and rewriting ORIGIN
 * globally would make every one of those canonicals depend on which hostname
 * happened to serve the request. That is a much larger change with a much
 * larger blast radius, to solve a problem only the house price pages have.
 *
 * So: one constant naming where the product's content lives, used only by the
 * pages that moved. Everything else keeps pointing at property-people.co.uk
 * exactly as before.
 *
 * WHY THE HOUSE PRICE PAGES MOVED. They carry real unique text - medians,
 * quartiles, ten years of trend, recent sales - and the same text on two
 * domains ranks on neither; a search engine picks one and discards the other,
 * or splits the credit between them. They were a week old and barely indexed
 * when this was decided, which is the cheapest moment it will ever be.
 *
 * CommonJS because api/render.js requires it directly, the same reason
 * seo.js and structuredData.js are.
 */

const AGENCY_ORIGIN = "https://property-people.co.uk";
const PRODUCT_ORIGIN = "https://doorstepdata.co.uk";

const BRANDS = {
    agency: {
        key: "agency",
        name: "Property People",
        origin: AGENCY_ORIGIN,
        tagline: "Established 2010 · South West London",
    },
    product: {
        key: "product",
        name: "Doorstep Data",
        origin: PRODUCT_ORIGIN,
        tagline: "Public record data for one address",
    },
};

/*
 * WHICH BRAND OWNS A DISTRICT.
 *
 * This replaced isProductPath, which said the whole of /house-prices belonged
 * to the product. That was true when there were 81 district pages and all of
 * them were South West London. It stopped being true when the set grew to
 * cover England and Wales.
 *
 * The reason for the split is commercial, not technical. "House prices in
 * SW17" is a LOCAL query with local intent - somebody who may be about to
 * sell or let - and the agency that actually transacts there should answer it.
 * Doorstep Data ranking for it won the page but could not convert the reader.
 * Everywhere Property People does not trade, the national product is the right
 * answer and the agency has no business claiming it.
 *
 * SO OWNERSHIP IS PER DISTRICT, not per path, and that is the whole of this
 * change. Five postcode areas are the agency's patch; the other 99 are the
 * product's. No district appears on both domains, which is what keeps the
 * duplicate-content problem solved - the same figures under two hostnames
 * rank on neither.
 *
 * The five areas are a deliberate overshoot of where Property People actually
 * sells. SW reaches Chelsea and KT reaches Oxshott, neither of which is
 * Tooting. The alternative is a hand-maintained list of individual districts
 * that somebody has to remember to update, and a page that silently changes
 * domain when they do. A whole postcode area is a line a reader can predict
 * and a rule that does not rot.
 */
const AGENCY_DISTRICT_AREAS = ["CR", "KT", "SM", "SW", "TW"];

/** "SW17" -> "SW", "KT1" -> "KT", "SW1P" -> "SW". */
function districtArea(district) {
    const m = String(district || "").toUpperCase().match(/^[A-Z]{1,2}/);
    return m ? m[0] : null;
}

/**
 * The brand that owns a postcode district's house price page.
 *
 * Anything unrecognised falls to the product, which is the safe direction: an
 * unknown district is far more likely to be somewhere in the other 99 areas
 * than in the five, and the product is the one making a national claim.
 */
function brandForDistrict(district) {
    const area = districtArea(district);
    return area && AGENCY_DISTRICT_AREAS.indexOf(area) !== -1 ? BRANDS.agency : BRANDS.product;
}

/** Where a district's page canonically lives. The one call most callers want. */
function originForDistrict(district) {
    return brandForDistrict(district).origin;
}

/**
 * The brand for a hostname. Anything that is not the product's domain is the
 * agency's, including localhost - a developer running the site locally is
 * working on Property People unless they say otherwise.
 */
function brandForHost(host) {
    const h = String(host || "").toLowerCase().split(":")[0];
    if (h === "doorstepdata.co.uk" || h === "www.doorstepdata.co.uk") return BRANDS.product;

    /*
     * `doorstepdata.localhost` is the product, so it can be run locally.
     *
     * Until this existed the product brand was unreachable off the live
     * domain: `localhost` is the agency by the rule below, so a developer
     * could not see the product homepage, its nav and footer, or the
     * valuation panel on `/property-report` without deploying. Checking a
     * product-only change against a phone viewport meant checking it in
     * production, which is not a check.
     *
     * `.localhost` IS SAFE IN A WAY A PATTERN MATCH WOULD NOT BE. It is
     * reserved by RFC 6761 and can never be a real public hostname, so this
     * cannot be reached by a request from the internet and cannot be spoofed
     * into flipping the brand on the live box - a Host header of
     * `doorstepdata.localhost` arriving at nginx matches no server block.
     * Matching something like "contains doorstepdata" would have been a real
     * hazard; this is closed by the name itself.
     *
     * Dev servers need the host check relaxed to answer on this name - see
     * DANGEROUSLY_DISABLE_HOST_CHECK in .claude/launch.json. That flag is
     * dev-only and reaches no build.
     */
    if (h === "doorstepdata.localhost" || h.endsWith(".doorstepdata.localhost")) {
        return BRANDS.product;
    }

    return BRANDS.agency;
}

/*
 * WHERE A VALUATION LEAD IS WORTH MOST.
 *
 * Two completely different numbers, and the postcode decides which:
 *
 *   Inside the five areas    the agency can value it itself. At the agency's
 *                            own 2.4% fee, one instruction that completes is
 *                            worth about £14,400 on a £600,000 house.
 *   Everywhere else          nobody here can value a house in Leeds. The lead
 *                            is worth what a local agent will pay for it,
 *                            which is £30-60.
 *
 * So the same form, filled in by two people on the same afternoon, produces
 * leads that differ by a factor of around three hundred - and until now both
 * went to info@ looking identical. That is the whole reason this function
 * exists: not to send the mail anywhere new, but to say which of the two
 * arrived, in the first line, so the valuable one is never worked as though
 * it were the cheap one.
 *
 * IT LIVES HERE because it is a district rule, and section 6f of CLAUDE.md
 * says district rules go through this file. It reuses AGENCY_DISTRICT_AREAS
 * rather than restating the list, so the trading area is defined exactly
 * once - re-cutting the areas re-cuts the routing with it.
 *
 * IT DELIBERATELY DOES NOT CHOOSE A RECIPIENT. The contact-form Lambda sends
 * to one fixed address and accepts a fixed set of fields, because it was an
 * open relay until October 2026 (section 6g). Letting the browser nominate
 * who an email goes to is precisely the hole that closed, and a lead is not
 * worth reopening it. Routing here means labelling, and a person or a later
 * server-side job acts on the label.
 */
const LEAD_ROUTES = {
    agency: {
        key: "agency",
        label: "OWN AREA - value it ourselves",
        /* What it is worth if it completes, for whoever reads the mail. */
        worth: "~£14,400 at 2.4% on a £600,000 sale",
        action: "Call them. This is an instruction to win, not a lead to sell.",
    },
    referral: {
        key: "referral",
        label: "OUTSIDE OUR AREA - sell to a local agent",
        worth: "£30-60 as a referral",
        action: "Pass to an agent covering this district, if consent was given.",
    },
};

/**
 * Which route a valuation lead takes, from the property's postcode.
 *
 * Takes a full postcode or a bare district - it only ever reads the area
 * letters. An unreadable postcode routes to `referral`, which is the safe
 * direction: the five areas are about 1% of England and Wales, so an unknown
 * is almost certainly outside them, and the cost of being wrong that way is
 * a cheap lead handled carefully rather than a £14,400 one sold for £40.
 */
function leadRoute(postcodeOrDistrict) {
    // Trimmed because this one arrives from a text input, not from a route
    // parameter: a leading space would otherwise read as an unknown area.
    const area = districtArea(String(postcodeOrDistrict || "").trim());
    return area && AGENCY_DISTRICT_AREAS.indexOf(area) !== -1
        ? LEAD_ROUTES.agency
        : LEAD_ROUTES.referral;
}

module.exports = {
    AGENCY_ORIGIN,
    PRODUCT_ORIGIN,
    BRANDS,
    brandForHost,
    AGENCY_DISTRICT_AREAS,
    districtArea,
    brandForDistrict,
    originForDistrict,
    LEAD_ROUTES,
    leadRoute,
};
